import React from 'react';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import { COLOR_TYPE } from '@/UIlibrary/globalTypes';
import { RequestStatus } from '@/graphql/__generated__/van-credits';
import { useTranslations } from 'use-intl';

interface RequestStatusBadgeProps {
	status: RequestStatus;
	size?: 'sm' | 'md' | 'lg';
}

export const RequestStatusBadge: React.FC<RequestStatusBadgeProps> = ({ status, size = 'sm' }) => {
	const t = useTranslations('VanCreditRequests');

	// Map statuses to badge color types from your UI library
	const statusStyles: Record<RequestStatus, COLOR_TYPE> = {
		CREATED: 'warning',
		PERSONAL_INFORMATION_REGISTERED: 'primary',
		ACTIVITY_INFORMATION_REGISTERED: 'primary',
		CREDIT_DETAILS_REGISTERED: 'primary',
		MONTHLY_BUSINESS_EXPENSES_REGISTERED: 'primary',
		MONTHLY_HOUSE_EXPENSES_REGISTERED: 'primary',
		MONTHLY_INCOME_INFORMATION_REGISTERED: 'primary',
		OTHER_CREDITS_REGISTERED: 'primary',
		VALUE_OF_ASSETS_REGISTERED: 'primary',
		EXPECTED_PROFIT_FOR_NEW_VEHICLES_REGISTERED: 'primary',
		COMPLETED: 'success',
		IN_REVIEW: 'warning',
		REJECTED: 'error',
		APPROVED_BY_CREDIT_COMMITTEE: 'success',
		PASSED_TO_CREDIT_COMMITTEE: 'primary',
		REJECTED_BY_CREDIT_COMMITTEE: 'error',
		SIGNED_DOCUMENTS_UPLOADED: 'primary'
	};

	// Map statuses to display names using translations
	const statusDisplayNames: Record<RequestStatus, string> = {
		CREATED: t('CreatedStatus'),
		PERSONAL_INFORMATION_REGISTERED: t('PersonalInformationStatus'),
		ACTIVITY_INFORMATION_REGISTERED: t('BusinessInformationStatus'),
		CREDIT_DETAILS_REGISTERED: t('CreditDetailsStatus'),
		MONTHLY_BUSINESS_EXPENSES_REGISTERED: t('BusinessExpensesStatus'),
		MONTHLY_HOUSE_EXPENSES_REGISTERED: t('HouseExpensesStatus'),
		MONTHLY_INCOME_INFORMATION_REGISTERED: t('IncomeInformationStatus'),
		OTHER_CREDITS_REGISTERED: t('OtherCreditsStatus'),
		VALUE_OF_ASSETS_REGISTERED: t('AssetsValueStatus'),
		EXPECTED_PROFIT_FOR_NEW_VEHICLES_REGISTERED: t('ExpectedProfitStatus'),
		COMPLETED: t('CompletedStatus'),
		IN_REVIEW: t('InReviewStatus'),
		REJECTED: t('RejectedStatus'),
		APPROVED_BY_CREDIT_COMMITTEE: t('ApprovedByCreditCommitteeStatus'),
		PASSED_TO_CREDIT_COMMITTEE: t('PassedToCreditCommitteeStatus'),
		REJECTED_BY_CREDIT_COMMITTEE: t('RejectedByCreditCommitteeStatus'),
		SIGNED_DOCUMENTS_UPLOADED: t('SignedDocumentsUploadedStatus')
	};

	const displayText = `${statusDisplayNames[status] || t('Status')}`;

	return <TextBadge text={displayText} size={size} type={statusStyles[status] || 'gray'} />;
};

// Optional: Progress step indicator component
// You can use this in detailed views to show where in the overall flow a request is
export const RequestStatusProgress: React.FC<{ currentStatus: RequestStatus }> = ({
	currentStatus
}) => {
	const t = useTranslations('VanCreditRequests');

	// Define the sequence of statuses in the application flow
	const statusFlow = [
		RequestStatus.CREATED,
		RequestStatus.PERSONAL_INFORMATION_REGISTERED,
		RequestStatus.ACTIVITY_INFORMATION_REGISTERED,
		RequestStatus.CREDIT_DETAILS_REGISTERED,
		RequestStatus.MONTHLY_INCOME_INFORMATION_REGISTERED,
		RequestStatus.MONTHLY_BUSINESS_EXPENSES_REGISTERED,
		RequestStatus.MONTHLY_HOUSE_EXPENSES_REGISTERED,
		RequestStatus.VALUE_OF_ASSETS_REGISTERED,
		RequestStatus.OTHER_CREDITS_REGISTERED,
		RequestStatus.COMPLETED,
		RequestStatus.IN_REVIEW,
		RequestStatus.REJECTED,
		RequestStatus.APPROVED_BY_CREDIT_COMMITTEE,
		RequestStatus.PASSED_TO_CREDIT_COMMITTEE,
		RequestStatus.REJECTED_BY_CREDIT_COMMITTEE,
		RequestStatus.SIGNED_DOCUMENTS_UPLOADED
	];

	// Map statuses to display names using translations
	const statusDisplayNames: Record<RequestStatus, string> = {
		CREATED: t('CreatedStatus'),
		PERSONAL_INFORMATION_REGISTERED: t('PersonalInformationStatus'),
		ACTIVITY_INFORMATION_REGISTERED: t('BusinessInformationStatus'),
		CREDIT_DETAILS_REGISTERED: t('CreditDetailsStatus'),
		MONTHLY_BUSINESS_EXPENSES_REGISTERED: t('BusinessExpensesStatus'),
		MONTHLY_HOUSE_EXPENSES_REGISTERED: t('HouseExpensesStatus'),
		MONTHLY_INCOME_INFORMATION_REGISTERED: t('IncomeInformationStatus'),
		OTHER_CREDITS_REGISTERED: t('OtherCreditsStatus'),
		VALUE_OF_ASSETS_REGISTERED: t('AssetsValueStatus'),
		EXPECTED_PROFIT_FOR_NEW_VEHICLES_REGISTERED: t('ExpectedProfitStatus'),
		COMPLETED: t('CompletedStatus'),
		IN_REVIEW: t('InReviewStatus'),
		REJECTED: t('RejectedStatus'),
		APPROVED_BY_CREDIT_COMMITTEE: t('ApprovedByCreditCommitteeStatus'),
		PASSED_TO_CREDIT_COMMITTEE: t('PassedToCreditCommitteeStatus'),
		REJECTED_BY_CREDIT_COMMITTEE: t('RejectedByCreditCommitteeStatus'),
		SIGNED_DOCUMENTS_UPLOADED: t('SignedDocumentsUploadedStatus')
	};

	const currentIndex = statusFlow.indexOf(currentStatus);
	const totalSteps = statusFlow.length;
	const progress = Math.round(((currentIndex + 1) / totalSteps) * 100);

	return (
		<div className="w-full">
			{/* Progress bar */}
			<div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
				<div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }} />
			</div>

			{/* Current status indicator */}
			<div className="flex justify-between items-center">
				<div className="text-sm text-gray-700">{statusDisplayNames[currentStatus]}</div>
				<div className="text-sm font-medium text-gray-700">
					{progress}% {t('Complete')}
				</div>
			</div>
		</div>
	);
};
